import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { useCaseStats } from '../api/hooks';
import { BrandMark, Icon, type IconName } from '../components/Icons';
import { ThemeToggle } from '../components/ThemeToggle';
import { ALL_CLINICS, ALL_LABS, HOME, modeOf } from '../lib/context';
import { changePassword, logout } from '../auth/userManager';
import { useSession } from './session';

type NavItem = [to: string, icon: IconName, labelKey: string];

export function Sidebar() {
  const { t } = useTranslation();
  const s = useSession();
  const navigate = useNavigate();
  const { mode, me, readOnly } = s;

  const items: NavItem[] =
    mode === 'lab'
      ? [
          ['/board', 'board', 'nav.board'],
          ['/partners', 'partners', 'nav.partners'],
        ]
      : mode === 'admin'
        ? [
            ['/admin/users', 'users', 'nav.users'],
            ['/admin/orgs', 'orgs', 'nav.orgs'],
            ['/admin/audit', 'audit', 'nav.audit'],
          ]
        : mode === 'doctor'
          ? [
              ['/cases', 'cases', 'nav.cases'],
              ['/patients', 'patients', 'nav.patients'],
              ...(readOnly ? [] : ([['/new', 'new', 'nav.newCase']] as NavItem[])),
            ]
          : [];

  const statsEnabled = mode === 'doctor' || mode === 'lab';
  const stats = useCaseStats(s.side, s.orgParam, statsEnabled);
  const count = !statsEnabled ? 0 : ((mode === 'lab' ? stats.data?.new : stats.data?.action) ?? 0);

  const clinics = me.memberships.filter((m) => m.org_type === 'clinic');
  const labs = me.memberships.filter((m) => m.org_type === 'lab');
  const showCtx = (mode === 'doctor' || mode === 'lab') && me.memberships.length > 0;

  const roleLabel =
    mode === 'admin'
      ? t('roles.admin')
      : mode === 'lab'
        ? t('roles.tech')
        : mode === 'doctor'
          ? t('roles.doctor')
          : t('roles.none');

  return (
    <aside className="side">
      <div className="brand">
        <BrandMark />
        <div>
          <div className="brand-name">{t('app.name')}</div>
          <div className="brand-sub">{t(`app.sub.${mode}`)}</div>
        </div>
      </div>

      {showCtx && (
        <label className="ctx">
          <span>{t('nav.org')}</span>
          <select
            value={s.ctx ?? ''}
            onChange={(e) => {
              const c = e.target.value;
              s.setCtx(c);
              navigate(HOME[modeOf(me, c)]);
            }}
          >
            {clinics.length > 0 && (
              <optgroup label={t('nav.clinicsGroup')}>
                {clinics.length > 1 && <option value={ALL_CLINICS}>{t('nav.allClinics')}</option>}
                {clinics.map((m) => (
                  <option key={m.org_id} value={m.org_id}>
                    {m.org_name}
                  </option>
                ))}
              </optgroup>
            )}
            {labs.length > 0 && (
              <optgroup label={t('nav.labsGroup')}>
                {labs.length > 1 && <option value={ALL_LABS}>{t('nav.allLabs')}</option>}
                {labs.map((m) => (
                  <option key={m.org_id} value={m.org_id}>
                    {m.org_name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </label>
      )}

      <nav className="nav" aria-label={t('nav.sections')}>
        {items.map(([to, icon, label], i) => (
          <NavLink key={to} to={to} end={to === '/new'}>
            <Icon name={icon} />
            <span className="lbl">{t(label)}</span>
            {i === 0 && count > 0 && (
              <span className="count" aria-label={t('nav.countHint', { count })}>
                {count}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="side-foot">
        <div className="who">
          <strong>{me.name}</strong>
          <span>
            {roleLabel}
            {readOnly ? t('nav.supportSuffix') : ''}
          </span>
        </div>
        <ThemeToggle />
        {!readOnly && (
          <div className="side-acts">
            <button type="button" className="btn btn-sm btn-quiet" onClick={() => void changePassword()}>
              {t('nav.changePassword')}
            </button>
            <button type="button" className="btn btn-sm btn-quiet" onClick={() => void logout()}>
              {t('nav.logout')}
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
